import { SelectedOptions } from '@shopify/hydrogen-react/ProductProvider';
import {
  ProductOption,
  ProductVariant,
  Product,
} from '@shopify/hydrogen-react/storefront-api-types';
import classNames from 'classnames';
import React from 'react';
import OptionColor from './OptionColor';
import OptionText from './OptionText';
import SelectBox from '../../../components/ui/SelectBox';
import {
  handleOptionSelect,
  optionCombinationExists,
  orderOptionValuesByVariants,
} from '../functions';

export enum ProductVariantOptions {
  COLOR = 'Color',
  SIZE = 'Size',
  LOGO = 'Logo',
}

interface Props {
  onSelectedOptions: (selectedOptions: any) => void;
  selectedOptions: SelectedOptions;
  selectedVariant: ProductVariant;
  product: Product;
  showOnly?: ProductVariantOptions;
  withInfo?: boolean;
}

const OptionsWrapper: React.FC<Props> = ({
  onSelectedOptions,
  showOnly,
  selectedOptions,
  selectedVariant, // eslint-disable-line no-unused-vars
  product,
  withInfo = true,
}) => {
  if (!product?.variants?.edges) {
    return null;
  }

  const availableOptions = product.variants.edges.map(item => {
    return item.node.selectedOptions;
  });

  const outOfStockOptions = product.variants.edges
    .filter(item => !item.node.availableForSale)
    .map(item => item.node.selectedOptions);

  const orderedOptions = product ? orderOptionValuesByVariants(product) : [];

  let options: ProductOption[] = showOnly
    ? orderedOptions.filter(option => {
        return option.name === showOnly;
      })
    : orderedOptions.filter(option => {
        return option.name !== 'Title';
      });

  return (
    <div className="product-options-wrapper">
      {options.map((option, index) => {
        let selectedOption = selectedOptions[option.name] ?? '';

        if (
          option.name.indexOf(ProductVariantOptions.COLOR) > -1 &&
          option.optionValues.length !== 24
        ) {
          return (
            <div
              className={classNames({
                'product-options': true,
                even: true,
                first: true,
                'no-border': !withInfo,
              })}
              key={index}
            >
              {withInfo && (
                <div className="product-options-info">
                  <div className="product-options-info-title">{option.name}</div>
                  <div className="product-options-info-selected">{selectedOption}</div>
                </div>
              )}
              <OptionColor
                onSelectedOptions={options => onSelectedOptions(options)}
                selectedOptions={selectedOptions as SelectedOptions}
                availableOptions={availableOptions}
                optionGroup={option}
              />
            </div>
          );
        } else if (option.optionValues.length === 24) {
          const selectedOption = option?.name && selectedOptions && selectedOptions[option.name];
          return (
            <div
              className={classNames({
                'product-options': true,
                even: true,
                'no-border': !withInfo,
              })}
              key={index}
            >
              {withInfo && (
                <div className="product-options-info">
                  <div className="product-options-info-title">{option.name}</div>
                  <div className="product-options-info-selected">{selectedOption}</div>
                </div>
              )}
              <SelectBox
                className="flex-1"
                options={option.optionValues.map(e => ({
                  label: e.name,
                  value: e.name,
                }))}
                onOptionChange={selectedOption => {
                  onSelectedOptions(
                    handleOptionSelect(selectedOptions, option.name, selectedOption.value as string)
                  );
                }}
                isOptionDisabled={singleOption => {
                  const isAvailable = optionCombinationExists(
                    availableOptions,
                    selectedOptions,
                    option.name,
                    singleOption.value as string
                  );
                  return !isAvailable;
                }}
              />
            </div>
          );
        }

        return (
          <div className="product-options even" key={index}>
            {withInfo && (
              <div className="product-options-info">
                <div className="product-options-info-title">{option.name}</div>
                <div className="product-options-info-selected">{selectedOption}</div>
              </div>
            )}
            <OptionText
              onSelectedOptions={options => onSelectedOptions(options)}
              selectedOptions={selectedOptions as SelectedOptions}
              availableOptions={availableOptions}
              outOfStockOptions={outOfStockOptions}
              optionGroup={option}
            />
          </div>
        );
      })}
    </div>
  );
};

export default OptionsWrapper;
